/**
 * Structured Logging Service for ChatBot
 *
 * Provides consistent, structured logging with context and error handling
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  readonly component?: string
  readonly caseId?: number | null
  readonly sessionId?: string
  readonly messageIndex?: number | null
  readonly [key: string]: unknown
}

interface LogEntry {
  readonly timestamp: string
  readonly level: LogLevel
  readonly message: string
  readonly context?: LogContext
  readonly error?: Error
}

class ChatBotLogger {
  private readonly component = 'ChatBot'

  private createLogEntry(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: context
        ? { component: this.component, ...context }
        : { component: this.component },
      error
    }
  }

  private log(entry: LogEntry): void {
    const { level, message, context, error } = entry
    const logMessage = `[${entry.timestamp}] ${level.toUpperCase()}: ${message}`

    switch (level) {
      case 'debug':
        console.debug(logMessage, { context, error })
        break
      case 'info':
        console.info(logMessage, { context, error })
        break
      case 'warn':
        console.warn(logMessage, { context, error })
        break
      case 'error':
        console.error(logMessage, { context, error })
        break
    }
  }

  debug(message: string, context?: LogContext): void {
    this.log(this.createLogEntry('debug', message, context))
  }

  info(message: string, context?: LogContext): void {
    this.log(this.createLogEntry('info', message, context))
  }

  warn(message: string, context?: LogContext): void {
    this.log(this.createLogEntry('warn', message, context))
  }

  error(message: string, context?: LogContext, error?: Error): void {
    this.log(this.createLogEntry('error', message, context, error))
  }

  /**
   * Log GraphQL subscription errors with detailed context
   */
  subscriptionError(error: Error, context?: LogContext): void {
    const enhancedContext = {
      ...context,
      errorType: 'GraphQL Subscription',
      graphQLErrors: (error as unknown as { graphQLErrors?: unknown })
        ?.graphQLErrors,
      networkError: (error as unknown as { networkError?: unknown })
        ?.networkError
    }

    this.error('GraphQL subscription failed', enhancedContext, error)

    // Log individual GraphQL errors for debugging
    const errorWithGraphQL = error as unknown as {
      graphQLErrors?: unknown[]
    }
    if (errorWithGraphQL?.graphQLErrors?.length) {
      errorWithGraphQL.graphQLErrors.forEach(
        (gqlError: unknown, index: number) => {
          this.error(`GraphQL Error ${index + 1}`, { ...context, gqlError })
        }
      )
    }
  }

  /**
   * Log session storage operations
   */
  sessionStorage(
    operation: 'get' | 'set' | 'error',
    key: string,
    context?: LogContext
  ): void {
    this.debug(`Session storage ${operation}`, { ...context, storageKey: key })
  }

  /**
   * Log message state transitions
   */
  messageTransition(
    from: string,
    to: string,
    messageIndex: number | null,
    context?: LogContext
  ): void {
    this.debug(`Message status transition: ${from} → ${to}`, {
      ...context,
      messageIndex,
      statusTransition: { from, to }
    })
  }
}

// Export singleton instance
export const logger = new ChatBotLogger()
export type { LogContext }
